import { notFound, redirect } from "next/navigation"
import { getServerOrClientCookies } from "../utils/cookies"
import { noPermissionToast } from "../utils/toast"
import { absoluteAPI } from "./constants"
import { isRedirectError } from "next/dist/client/components/redirect-error"

/**
 * Custom error class for API errors
 */
export class APIError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public url: string,
    public data?: any,
    public originalError?: Error
  ) {
    super(
      `HTTP error! Status: ${status} - ${statusText} at URL ${url} with data: ${JSON.stringify(
        data
      )}`
    )
    this.name = "APIError"
    if (originalError && !this.stack) {
      this.stack = originalError.stack
    }

    if (originalError && !this.cause) {
      this.cause = originalError.cause
    }
  }

  /** Helper method to handle 404 errors.
   *
   * Wrap the customFetch in a try/catch whenever we want to
   *  handle 404 errors.
   *
   *  We don't do this down below, because some API calls
   *  return 404s but we wouldn't redirect the entire page.
   */
  static handle404Error(error: unknown): never {
    if (error instanceof APIError && error.status === 404) {
      console.warn("Returning notFound due to 404:", error)
      return notFound()
    }

    throw error
  }
}

/**
 * Handle a 423 error that occurs if a user is banned.
 *
 */
export async function handle423() {
  // On the server-side, if the user is hitting a 403,
  // prompt them to login.
  if (typeof window === "undefined") {
    console.error("Redirecting to /disabled_account due to 423")
    return redirect("/disabled_account")
  }

  window.location.href = "/disabled_account"
}

/**
 * Handle a 403 error.
 *
 * This displays a toast if their permissions are wrong;
 * otherwise, it redirects to /login.
 */
async function handle403(res: any) {
  // On the server-side, if the user is hitting a 403,
  // try refreshing token
  if (typeof window === "undefined") {
    console.error("handle403: Redirecting to refresh due to 403")
    return redirect("/refresh")
  }

  const data = await res.json()

  if ("actionId" in data && "msg" in data) {
    // If the user is trying to impersonate another team, and they don't have permission,
    // show a toast.
    noPermissionToast(data.msg)
    throw new APIError(403, "Forbidden", res.url, data)
  }

  // Otherwise, we're on the client-side, and
  // the user is doing something that's causing a 403 (eg their cookie is messed up?),
  // so redirect to /refresh.
  window.location.href = "/refresh"
}

/**
 * Handle a 302 redirect.
 *
 * This handles a redirect.
 */
async function handle302(res: any) {
  const data = await res.json()

  // msg holds the redirect URL
  if (data?.msg) return redirect(data.msg)
  console.error("Redirect is missing redirect path", data)
}

/**
 * This fetch function can be used by both the server and the client.
 */
export default async function customFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  fetchOptions?: {
    /**
     * If we want to ignore impersonation (eg we ONLY fetch
     * data of the user who is logged in; ignoring the team they're working on),
     * pass this as true.
     */
    ignoreImpersonation?: boolean

    /**
     * If we want to completely bypass cookies (eg just fetch regular static data,
     * with no cookies in the request).
     *
     * This is helpful when we do NOT want to include the blogId in the query string
     * (eg for impersonation), so the cache key is the same for all users.
     */
    bypassCookies?: boolean
  }
): Promise<T> {
  const url = new URL(absoluteAPI + endpoint)

  let privyToken, privyIdToken, jwt, activeTeamId

  // NOTE: The cookies() calls in the below methods should NOT be wrapped in a try/catch,
  // or else this error will occur and PPR will bail: https://nextjs.org/docs/messages/ppr-caught-error
  if (fetchOptions?.bypassCookies) {
    options.credentials = "omit"
  } else {
    /**
     * If the user is logged in or not.
     */
    const [_privyToken, _privyIdToken, _jwt] = await Promise.all([
      getServerOrClientCookies("privy-token"),
      getServerOrClientCookies("privy-id-token"),
      getServerOrClientCookies("jwt"),
    ])
    privyToken = _privyToken
    privyIdToken = _privyIdToken
    jwt = _jwt

    /**
     * Which team the user is currently working on.
     */
    activeTeamId = await getServerOrClientCookies("activeTeamId")

    options.credentials = "include"
  }

  try {
    // No need for special JWT handling on the client-side, as the browser
    // includes it below if credentials is set to 'include'

    // Add activeTeamId to the query string if it exists
    if (activeTeamId && !fetchOptions?.ignoreImpersonation) {
      url.searchParams.append("blogId", activeTeamId)
    }

    // On the server-side, need to explicitly pass the JWT.
    if (privyToken || jwt) {
      options.headers = {
        ...options.headers,
        Cookie: privyToken
          ? `privy-token=${privyToken}; privy-id-token=${privyIdToken}`
          : `jwt=${jwt}`,
      }
    }

    const res = await fetch(url, options)

    // Check if the response status is 403
    if (res.status === 403) {
      console.error("Request failed with status 403", url.toString())
      await handle403(res)
    }

    // User is banned!
    if (res.status === 423) {
      await handle423()
    }

    // Check if the response status is 302
    if (res.status === 302) await handle302(res)

    // If the response is a 204 no content, return null. If we try to parse JSON, it will throw an error.
    if (res.status === 204) {
      return null as T
    }

    // Attempt to parse JSON response. Do this even for error responses as there might be a `msg` field with valuable information as to the cause of the error.
    let parsedBody
    try {
      parsedBody = (await res.json()) as T
    } catch {
      console.error("Failed to parse JSON", {
        url: url.toString(),
        method: options.method,
        res,
      })

      throw new Error("Failed to parse JSON")
    }

    if (!res.ok && res.status !== 404) {
      console.error(
        `HTTP error! Status: ${res.status} - ${res.statusText}`,
        parsedBody
      )
    }

    if (!res.ok) {
      console.log(
        `HTTP error! Status: ${res.status} - ${res.statusText}`,
        parsedBody
      )

      // Handle non-OK HTTP responses after trying to parse the body, so we can see what the server's error message is, if any.
      throw new APIError(res.status, res.statusText, url.toString(), parsedBody)
    }

    return parsedBody
  } catch (error) {
    // If APIError or redirect error, rethrow it
    if (error instanceof APIError || isRedirectError(error)) throw error
    if (error instanceof Error) {
      console.warn(`Fetch error: ${endpoint}`, error.message)
      throw new APIError(
        500,
        "Internal Server Error",
        url.toString(),
        {
          originalName: error.name,
          originalErrorMessage: error.message,
          originalCause: error.cause,
        },
        error
      )
    }
    console.warn("Unexpected error:", error)
    throw new APIError(500, "Unknown Error", url.toString(), {
      originalError: error,
    })
  }
}
